import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';

import { CREATABLE, SCENE_STARTERS, uniqueAssetPath, type CreatableAsset } from '../../lib/asset-create.js';
import { TYPE_FILTERS, formatBytes, matchesAssetFilters, parentOf, parseAssetQuery, sortAssetEntries } from '../../lib/asset-search.js';
import { assetDisplayName, beginAssetDrag, draggedAssetPath, endAssetDrag, fileTypeName } from '../../lib/asset-types.js';
import { ApiError, api } from '../../lib/api.js';
import { bytesToBase64 } from '../../lib/pixel-doc.js';
import { CONVERTIBLE_SUFFIXES, hasExternalFiles, importExternalFiles } from '../../lib/asset-import.js';
import { editImportedModel, openAsset } from '../../lib/open-asset.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { rawUrl } from '../../lib/thumbnails.js';
import type { TreeEntry } from '../../lib/tree.js';
import { allLabels, LABELS_FILE, TILE_MAX, TILE_MIN, useAssets, type AssetsSort } from '../../state/assets.js';
import { logEditor } from '../../state/console.js';
import { assetKind, useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { ContextMenu, IconButton, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { ASSET_ICON, AssetThumb } from './AssetThumb.js';
import '../scene/fields/asset-field.css';
import './assets.css';
import { prefabSceneItems } from '../scene/prefab-menus.js';
import { usePrefabs } from '../../state/prefabs.js';

export { ASSET_ICON } from './AssetThumb.js';

/** Blender's file browser "Sort By" choices. */
const SORTS: { id: AssetsSort; label: string }[] = [
  { id: 'name', label: 'Name' },
  { id: 'type', label: 'Type' },
  { id: 'modified', label: 'Modified date' },
  { id: 'size', label: 'Size' },
];

const sameTypes = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((t) => b.includes(t));

type Section = { kind: 'folder' } | { kind: 'favorites' } | { kind: 'label'; label: string };

/**
 * Project browser (Unity's two-column Project window / Blender's asset
 * browser): favorites, labels and the folder tree on the left; thumbnails
 * (grid with a size slider, or a list) on the right; type filters and
 * `t:` / `l:` search; Create menu; drag files into the scene, the hierarchy,
 * asset fields or another folder.
 */
export function Assets() {
  const entries = useProjectFiles((s) => s.entries);
  const loading = useProjectFiles((s) => s.loading);
  const load = useProjectFiles((s) => s.load);
  const projectId = useWorkspace((s) => s.projectId);
  const projectName = useWorkspace((s) => s.projectName);
  const favorites = useAssets((s) => s.favorites);
  const savedSearches = useAssets((s) => s.savedSearches);
  const labels = useAssets((s) => s.labels);
  const view = useAssets((s) => s.view);
  const ping = useAssets((s) => s.ping);
  const setView = useAssets((s) => s.setView);

  const [folder, setFolder] = useState('');
  const [query, setQuery] = useState('');
  const [section, setSection] = useState<Section>({ kind: 'folder' });
  const [selected, setSelected] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; entry: TreeEntry | null } | null>(null);
  const [dropFolder, setDropFolder] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [openFolders, setOpenFolders] = useState<Set<string>>(() => new Set(['']));
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    useAssets.getState().setProject(projectId);
  }, [projectId]);

  const labelsEntry = entries.find((e) => e.path === LABELS_FILE);
  useEffect(() => {
    void useAssets.getState().loadLabels(labelsEntry?.modifiedAt ?? null);
  }, [labelsEntry?.modifiedAt, projectId]);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ templates: { id: string; name: string }[] }>('/engine/scene-templates')
      .then((r) => !cancelled && setTemplates(r.templates))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Ping from an asset field: reveal the file in its folder and flash it.
  useEffect(() => {
    if (!ping) return;
    const parent = parentOf(ping.path);
    setSection({ kind: 'folder' });
    setFolder(parent);
    setQuery('');
    setSelected(ping.path);
    setFlash(ping.path);
    setOpenFolders((open) => {
      const next = new Set(open);
      parent.split('/').forEach((_, i, parts) => next.add(parts.slice(0, i + 1).join('/')));
      return next;
    });
    const timer = setTimeout(() => setFlash(null), 1200);
    requestAnimationFrame(() =>
      gridRef.current?.querySelector(`[data-path="${CSS.escape(ping.path)}"]`)?.scrollIntoView({ block: 'nearest' }),
    );
    return () => clearTimeout(timer);
  }, [ping]);

  const visible = useMemo(() => entries.filter((e) => e.path !== LABELS_FILE), [entries]);
  const folders = useMemo(() => visible.filter((e) => e.kind === 'directory').map((e) => e.path).sort((a, b) => a.localeCompare(b)), [visible]);
  const versions = useMemo(() => new Map(entries.map((e) => [e.path, e.modifiedAt])), [entries]);

  const parsed = useMemo(() => parseAssetQuery(query), [query]);
  const searching = parsed.words.length + parsed.types.length + parsed.labels.length > 0 || section.kind !== 'folder';

  const items = useMemo(() => {
    const filters = { types: view.types, queryTypes: parsed.types, labels: parsed.labels, words: parsed.words };
    let list: TreeEntry[];
    if (searching) {
      list = visible.filter((e) => {
        if (section.kind === 'favorites' && !favorites.includes(e.path)) return false;
        if (section.kind === 'label' && !(labels[e.path] ?? []).includes(section.label)) return false;
        if (e.kind === 'directory') return section.kind === 'favorites' && !parsed.words.length && !parsed.types.length && !view.types.length;
        return matchesAssetFilters(e.path, labels[e.path] ?? [], filters);
      });
    } else {
      list = visible.filter(
        (e) => parentOf(e.path) === folder && (e.kind === 'directory' || matchesAssetFilters(e.path, labels[e.path] ?? [], filters)),
      );
    }
    return sortAssetEntries(list, view.sort, view.sortDesc);
  }, [visible, folder, searching, section, favorites, labels, parsed, view.types, view.sort, view.sortDesc]);

  const crumbs = folder ? folder.split('/') : [];
  const taken = useMemo(() => new Set(entries.map((e) => e.path)), [entries]);
  const selectedEntry = selected ? entries.find((e) => e.path === selected) : undefined;

  function openFolder(path: string) {
    setSection({ kind: 'folder' });
    setFolder(path);
    setQuery('');
    setOpenFolders((open) => new Set(open).add(path));
  }

  function activate(entry: TreeEntry) {
    if (entry.kind === 'directory') openFolder(entry.path);
    else openAsset(entry.path);
  }

  const refresh = () => {
    if (projectId) void load(projectId);
  };
  const fail = (what: string, error: unknown) =>
    logEditor('error', `${what}: ${error instanceof ApiError || error instanceof Error ? error.message : String(error)}`);

  async function create(item: CreatableAsset, where = folder) {
    if (!projectId) return;
    const name = await promptText(`New ${item.label.toLowerCase()} name`, item.defaultName);
    if (!name?.trim()) return;
    const path = uniqueAssetPath(taken, where, name.trim(), item.suffix);
    try {
      await api.put(`/projects/${projectId}/file`, { path, content: item.content(assetDisplayName(path)) });
      await load(projectId);
      setSelected(path);
      openAsset(path);
    } catch (error) {
      fail(`Could not create ${path}`, error);
    }
  }

  async function createScene(starter: { id: string; label: string; template?: string; content?: (name: string) => string }, where = folder) {
    if (!projectId) return;
    const name = await promptText('New scene name', 'New Scene');
    if (!name?.trim()) return;
    const path = uniqueAssetPath(taken, where || 'scenes', name.trim(), '.scene.json');
    try {
      if (starter.template) {
        const res = await api.post<{ copied: string[] }>(`/projects/${projectId}/scenes/from-template`, { template: starter.template, path, name: name.trim() });
        if (res.copied.length) logEditor('info', `Scene template also added: ${res.copied.join(', ')}`);
      } else {
        await api.put(`/projects/${projectId}/file`, { path, content: starter.content!(name.trim()) });
      }
      await load(projectId);
      setSelected(path);
      openAsset(path);
    } catch (error) {
      fail(`Could not create ${path}`, error);
    }
  }

  async function newFolder(where = folder) {
    if (!projectId) return;
    const name = await promptText('New folder name', 'New Folder');
    if (!name?.trim()) return;
    const path = uniqueAssetPath(taken, where, name.trim(), '');
    try {
      await api.post(`/projects/${projectId}/mkdir`, { path });
      await load(projectId);
      openFolder(path);
    } catch (error) {
      fail(`Could not create ${path}`, error);
    }
  }

  async function move(from: string, to: string) {
    if (!projectId || from === to) return;
    try {
      await api.post(`/projects/${projectId}/move`, { from, to });
      const fileLabels = labels[from];
      if (fileLabels) {
        await useAssets.getState().setLabels(from, []);
        await useAssets.getState().setLabels(to, fileLabels);
      }
      if (favorites.includes(from)) {
        useAssets.getState().toggleFavorite(from);
        useAssets.getState().toggleFavorite(to);
      }
      await load(projectId);
      setSelected(to);
    } catch (error) {
      fail(`Could not move ${from}`, error);
    }
  }

  async function rename(entry: TreeEntry) {
    const kind = assetKind(entry);
    const display = entry.kind === 'directory' ? entry.name : assetDisplayName(entry.path);
    const suffix = entry.name.slice(display.length);
    const next = await promptText('Rename', display);
    if (!next?.trim() || next.trim() === display) return;
    const to = `${parentOf(entry.path) ? `${parentOf(entry.path)}/` : ''}${next.trim()}${kind === 'folder' ? '' : suffix}`;
    if (taken.has(to)) return logEditor('warn', `${to} already exists.`);
    await move(entry.path, to);
  }

  async function duplicate(entry: TreeEntry) {
    if (!projectId) return;
    const display = assetDisplayName(entry.path);
    const to = uniqueAssetPath(taken, parentOf(entry.path), display, entry.name.slice(display.length));
    try {
      const bytes = new Uint8Array(await (await fetch(rawUrl(projectId, entry.path), { credentials: 'same-origin' })).arrayBuffer());
      await api.put(`/projects/${projectId}/file`, { path: to, content: bytesToBase64(bytes), encoding: 'base64' });
      await load(projectId);
      setSelected(to);
    } catch (error) {
      fail(`Could not duplicate ${entry.path}`, error);
    }
  }

  async function remove(entry: TreeEntry) {
    if (!projectId) return;
    const what = entry.kind === 'directory' ? `the folder ${entry.path} and everything in it` : entry.path;
    if (!window.confirm(`Delete ${what}? This cannot be undone here (use git to restore it).`)) return;
    try {
      await api.del(`/projects/${projectId}/file?path=${encodeURIComponent(entry.path)}`);
      if (labels[entry.path]) await useAssets.getState().setLabels(entry.path, []);
      if (favorites.includes(entry.path)) useAssets.getState().toggleFavorite(entry.path);
      setSelected(null);
      await load(projectId);
    } catch (error) {
      fail(`Could not delete ${entry.path}`, error);
    }
  }

  async function editLabels(path: string) {
    const current = labels[path] ?? [];
    const next = await promptText(`Labels for ${assetDisplayName(path)} (comma-separated)`, current.join(', '));
    if (next === null) return;
    await useAssets.getState().setLabels(path, next.split(','));
  }

  async function saveCurrentSearch() {
    const name = await promptText('Save search as', query.trim() || view.types.join(', '));
    if (!name?.trim()) return;
    useAssets.getState().saveSearch({ name: name.trim(), query, types: view.types });
  }

  /** OS files into `where` (FBX / OBJ / USD / Alembic are converted to GLB). */
  async function importFiles(where: string, files: File[]) {
    if (!projectId || !files.length) return;
    const created = await importExternalFiles(projectId, where, files);
    await load(projectId);
    const last = created.at(-1);
    if (last) setSelected(last);
  }
  function pickImport(where: string) {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ['.glb', '.gltf', ...CONVERTIBLE_SUFFIXES, '.mtl', '.png', '.jpg', '.jpeg', '.wav', '.ogg', '.mp3'].join(',');
    input.onchange = () => void importFiles(where, Array.from(input.files ?? []));
    input.click();
  }
  /** Copy a file or folder from a typed absolute path into `where`. */
  async function importFromPath(where: string) {
    const source = (await promptText(`Copy a file or folder from this computer into ${where || 'the project root'} (absolute path)`, ''))?.trim();
    if (!source || projectId === null) return;
    try {
      const r = await api.post<{ path: string }>(`/projects/${projectId}/import-path`, { source, dest: where });
      refresh();
      logEditor('info', `Imported ${r.path}`);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : 'Import failed.');
    }
  }

  function createItems(where: string): MenuItem[] {
    return [
      { id: 'h-create', label: where ? `Create in ${where}` : 'Create', heading: true },
      { id: 'folder', label: 'Folder', icon: 'folder', run: () => void newFolder(where) },
      { id: 'import', label: 'Import files… (models, textures, audio)', icon: 'arrowDown', run: () => pickImport(where) },
      { id: 'import-path', label: 'Import from disk path… (fonts, folders)', icon: 'folder', run: () => void importFromPath(where) },
      ...SCENE_STARTERS.map((s) => ({ id: `scene-${s.id}`, label: `Scene: ${s.label}`, icon: 'scene' as const, run: () => void createScene(s, where) })),
      ...templates.map((t) => ({
        id: `tpl-${t.id}`,
        label: `Scene: ${t.name} template`,
        icon: 'scene' as const,
        run: () => void createScene({ id: t.id, label: t.name, template: t.id }, where),
      })),
      { id: 'sep-c', label: '', separator: true },
      ...CREATABLE.map((c) => ({ id: c.id, label: c.label, icon: c.icon, run: () => void create(c, where) })),
    ];
  }

  function menuItems(entry: TreeEntry | null): MenuItem[] {
    if (!entry) return [...createItems(folder), { id: 'sep-r', label: '', separator: true }, { id: 'refresh', label: 'Refresh', icon: 'refresh', run: refresh }];
    const kind = assetKind(entry);
    const fav = favorites.includes(entry.path);
    return [
      { id: 'open', label: entry.kind === 'directory' ? 'Open folder' : 'Open', icon: ASSET_ICON[kind], run: () => activate(entry) },
      ...(kind === 'scene' || kind === 'model' || kind === 'pixel'
        ? [{ id: 'text', label: 'Open as JSON', icon: 'code' as const, run: () => openAsset(entry.path, { asText: true }) }]
        : []),
      ...(/\.controller\.json$/i.test(entry.name)
        ? [
            { id: 'edit-controller', label: 'Edit Animator Controller', icon: 'play' as const, run: () => openAsset(entry.path) },
            { id: 'text', label: 'Open as JSON', icon: 'code' as const, run: () => openAsset(entry.path, { asText: true }) },
          ]
        : []),
      ...(/\.glb$/i.test(entry.name)
        ? [
            { id: 'edit-model', label: 'Edit in Model tab', icon: 'model' as const, run: () => void editImportedModel(entry.path) },
            { id: 'reconvert', label: 'Convert to a new editable model', icon: 'refresh' as const, run: () => void editImportedModel(entry.path, { fresh: true }) },
          ]
        : []),
      // Prefabs and scenes (prefab workflow / multi-scene editing).
      ...prefabSceneItems(entry.path),
      ...(searching && entry.kind === 'file'
        ? [{ id: 'reveal', label: 'Show in folder', icon: 'folderOpen' as const, run: () => useAssets.getState().pingAsset(entry.path) }]
        : []),
      { id: 'sep1', label: '', separator: true },
      { id: 'fav', label: fav ? 'Remove from favorites' : 'Add to favorites', icon: fav ? 'starFilled' : 'star', run: () => useAssets.getState().toggleFavorite(entry.path) },
      ...(entry.kind === 'file' ? [{ id: 'labels', label: 'Labels…', icon: 'tag' as const, run: () => void editLabels(entry.path) }] : []),
      { id: 'rename', label: 'Rename…', icon: 'edit', shortcut: 'F2', run: () => void rename(entry) },
      ...(entry.kind === 'file' ? [{ id: 'dup', label: 'Duplicate', icon: 'copy' as const, run: () => void duplicate(entry) }] : []),
      { id: 'copy', label: 'Copy path', icon: 'copy', run: () => void navigator.clipboard?.writeText(entry.path) },
      { id: 'del', label: 'Delete…', icon: 'trash', danger: true, run: () => void remove(entry) },
      ...(entry.kind === 'directory' ? [{ id: 'sep2', label: '', separator: true }, ...createItems(entry.path)] : []),
    ];
  }

  /** Folders accept project files dragged onto them (move). */
  const folderDrop = (target: string) => ({
    onDragOver: (e: DragEvent) => {
      // A Hierarchy entity dropped on a folder becomes a prefab there.
      if (e.dataTransfer.types.includes('application/x-prism-entity')) {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setDropFolder(target);
        return;
      }
      const path = draggedAssetPath(e.dataTransfer);
      if (!path || path === target || parentOf(path) === target || target.startsWith(`${path}/`)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setDropFolder(target);
    },
    onDragLeave: () => setDropFolder((f) => (f === target ? null : f)),
    onDrop: (e: DragEvent) => {
      const entity = e.dataTransfer.getData('application/x-prism-entity');
      if (entity) {
        setDropFolder(null);
        e.preventDefault();
        e.stopPropagation();
        void usePrefabs.getState().createFromEntity(entity, target || 'prefabs');
        return;
      }
      const path = draggedAssetPath(e.dataTransfer);
      setDropFolder(null);
      if (!path || path === target || parentOf(path) === target || target.startsWith(`${path}/`)) return;
      e.preventDefault();
      e.stopPropagation();
      void move(path, `${target ? `${target}/` : ''}${path.slice(path.lastIndexOf('/') + 1)}`);
    },
  });

  const projectLabels = allLabels(labels);
  const list = view.mode === 'list';

  return (
    <div className="assets assets-browser">
      <aside className="assets-side" aria-label="Asset sections">
        <div className="assets-side-head">Favorites</div>
        <button
          type="button"
          className={`assets-side-item${section.kind === 'favorites' ? ' is-active' : ''}`}
          onClick={() => setSection({ kind: 'favorites' })}
        >
          <Icon name="starFilled" size={13} /> All favorites <span className="assets-side-count">{favorites.length || ''}</span>
        </button>
        {savedSearches.map((saved) => (
          <div key={saved.name} className="assets-side-row">
            <button
              type="button"
              className={`assets-side-item${section.kind === 'folder' && query === saved.query && sameTypes(view.types, saved.types) ? ' is-active' : ''}`}
              title={`${saved.query}${saved.types.length ? ` · types: ${saved.types.join(', ')}` : ''}`}
              onClick={() => {
                setSection({ kind: 'folder' });
                setQuery(saved.query);
                setView({ types: saved.types });
              }}
            >
              <Icon name="search" size={13} /> {saved.name}
            </button>
            <button
              type="button"
              className="assets-side-remove"
              title="Remove saved search"
              aria-label={`Remove saved search ${saved.name}`}
              onClick={() => useAssets.getState().removeSearch(saved.name)}
            >
              <Icon name="x" size={10} />
            </button>
          </div>
        ))}
        {projectLabels.length > 0 && <div className="assets-side-head">Labels</div>}
        {projectLabels.map((label) => (
          <button
            key={label}
            type="button"
            className={`assets-side-item${section.kind === 'label' && section.label === label ? ' is-active' : ''}`}
            onClick={() => setSection({ kind: 'label', label })}
          >
            <Icon name="tag" size={13} /> {label}
          </button>
        ))}
        <div className="assets-side-head">Folders</div>
        <FolderTree
          folders={folders}
          rootName={projectName || 'Project'}
          current={section.kind === 'folder' ? folder : null}
          open={openFolders}
          dropTarget={dropFolder}
          onOpen={openFolder}
          onToggle={(path) =>
            setOpenFolders((open) => {
              const next = new Set(open);
              if (next.has(path)) next.delete(path);
              else next.add(path);
              return next;
            })
          }
          dropProps={folderDrop}
        />
      </aside>

      <div
        className={`assets-main${importing ? ' is-importing' : ''}`}
        onDragOver={(e) => {
          if (!hasExternalFiles(e.dataTransfer)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setImporting(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setImporting(false);
        }}
        onDrop={(e) => {
          setImporting(false);
          if (!hasExternalFiles(e.dataTransfer)) return;
          e.preventDefault();
          void importFiles(section.kind === 'folder' ? folder : '', Array.from(e.dataTransfer.files));
        }}
      >
        <div className="assets-bar">
          <MenuButton items={() => createItems(folder)} className="ibtn assets-create" title="Create asset">
            <Icon name="plus" size={14} />
          </MenuButton>
          <nav className="crumbs" aria-label="Folder">
            {section.kind === 'folder' ? (
              <>
                <button type="button" className="crumb-btn" onClick={() => openFolder('')} {...folderDrop('')}>
                  <Icon name="folder" size={13} />
                  {projectName || 'Project'}
                </button>
                {crumbs.map((c, i) => (
                  <span key={i} className="crumb-part">
                    <Icon name="chevronRight" size={11} />
                    <button type="button" className="crumb-btn" onClick={() => openFolder(crumbs.slice(0, i + 1).join('/'))}>
                      {c}
                    </button>
                  </span>
                ))}
              </>
            ) : (
              <button type="button" className="crumb-btn" onClick={() => setSection({ kind: 'folder' })} title="Back to folders">
                <Icon name={section.kind === 'favorites' ? 'starFilled' : 'tag'} size={13} />
                {section.kind === 'favorites' ? 'Favorites' : `Label: ${section.label}`}
              </button>
            )}
          </nav>
          <div className="search-input compact assets-search">
            <Icon name="search" size={12} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search (t:material l:label)"
              aria-label="Search assets"
              title="Words match names and labels; t:<type> filters by type (t:material, t:texture, t:prefab...), l:<label> by label"
            />
            {(query || view.types.length > 0) && (
              <button
                type="button"
                className="assets-search-clear"
                title="Save search to Favorites"
                aria-label="Save search to Favorites"
                onClick={() => void saveCurrentSearch()}
              >
                <Icon name="star" size={11} />
              </button>
            )}
            {query && (
              <button type="button" className="assets-search-clear" aria-label="Clear search" onClick={() => setQuery('')}>
                <Icon name="x" size={11} />
              </button>
            )}
          </div>
          <MenuButton
            className={`ibtn${view.types.length ? ' is-active' : ''}`}
            title={view.types.length ? `Types: ${view.types.join(', ')}` : 'Filter by type'}
            items={() => [
              { id: 'h', label: 'Show types', heading: true },
              { id: 'all', label: 'All types', icon: view.types.length ? undefined : 'check', run: () => setView({ types: [] }) },
              ...TYPE_FILTERS.map((f) => ({
                id: f.id,
                label: f.label,
                icon: view.types.includes(f.id) ? ('check' as const) : f.icon,
                run: () => setView({ types: view.types.includes(f.id) ? view.types.filter((t) => t !== f.id) : [...view.types, f.id] }),
              })),
            ]}
          >
            <Icon name="layers" size={14} />
          </MenuButton>
          <MenuButton
            className="ibtn"
            title="Display: sort order and names"
            items={() => [
              { id: 'h-sort', label: 'Sort by', heading: true },
              ...SORTS.map((o) => ({
                id: `sort-${o.id}`,
                label: o.label,
                icon: view.sort === o.id ? ('check' as const) : undefined,
                run: () => setView({ sort: o.id }),
              })),
              { id: 'desc', label: 'Descending', icon: view.sortDesc ? ('check' as const) : undefined, run: () => setView({ sortDesc: !view.sortDesc }) },
              { id: 'sep', label: '', separator: true },
              { id: 'names', label: 'Show names', icon: view.showNames ? ('check' as const) : undefined, run: () => setView({ showNames: !view.showNames }) },
            ]}
          >
            <Icon name="arrowDown" size={14} />
          </MenuButton>
          <div className="segmented assets-mode">
            <IconButton icon="grid" title="Grid" size={13} active={!list} onClick={() => setView({ mode: 'grid' })} />
            <IconButton icon="list" title="List" size={13} active={list} onClick={() => setView({ mode: 'list' })} />
          </div>
          {!list && (
            <input
              type="range"
              className="asset-size-slider"
              min={TILE_MIN}
              max={TILE_MAX}
              value={view.size}
              title="Thumbnail size"
              aria-label="Thumbnail size"
              onChange={(e) => setView({ size: Number(e.target.value) })}
            />
          )}
          <IconButton icon="refresh" title="Refresh" size={13} onClick={refresh} />
        </div>

        <div
          ref={gridRef}
          className={list ? 'assets-list' : 'assets-grid'}
          style={list ? undefined : { gridTemplateColumns: `repeat(auto-fill, minmax(${view.size + 22}px, 1fr))` }}
          role="listbox"
          aria-label="Assets"
          onClick={(e) => e.target === e.currentTarget && setSelected(null)}
          onContextMenu={(e) => {
            if (e.target !== e.currentTarget) return;
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY, entry: null });
          }}
          {...(searching ? {} : folderDrop(folder))}
        >
          {folder && !searching && (
            <button
              type="button"
              className={`asset-tile is-up${dropFolder === parentOf(folder) ? ' is-drop' : ''}`}
              onDoubleClick={() => openFolder(parentOf(folder))}
              title="Up one folder"
              {...folderDrop(parentOf(folder))}
            >
              <span className="asset-icon kind-folder" style={list ? undefined : { width: view.size, height: view.size }}>
                <Icon name="folderOpen" size={list ? 14 : Math.round(view.size * 0.48)} />
              </span>
              <span className="asset-name">..</span>
            </button>
          )}
          {items.map((entry) => {
            const kind = assetKind(entry);
            const isDir = entry.kind === 'directory';
            const fileLabels = labels[entry.path] ?? [];
            const fav = favorites.includes(entry.path);
            const typeName = isDir ? 'Folder' : (fileTypeName(entry.path) ?? kind);
            return (
              <button
                key={entry.path}
                type="button"
                role="option"
                data-path={entry.path}
                aria-selected={selected === entry.path}
                className={`asset-tile${selected === entry.path ? ' is-selected' : ''}${flash === entry.path ? ' is-pinged' : ''}${dropFolder === entry.path ? ' is-drop' : ''}`}
                title={`${entry.path}${fileLabels.length ? `\nLabels: ${fileLabels.join(', ')}` : ''}`}
                onClick={() => setSelected(entry.path)}
                onDoubleClick={() => activate(entry)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') activate(entry);
                  else if (e.key === 'F2') void rename(entry);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setSelected(entry.path);
                  setMenu({ x: e.clientX, y: e.clientY, entry });
                }}
                draggable
                onDragStart={(e) => beginAssetDrag(e.dataTransfer, entry.path)}
                onDragEnd={endAssetDrag}
                {...(isDir ? folderDrop(entry.path) : {})}
              >
                {projectId && (
                  <AssetThumb
                    projectId={projectId}
                    path={entry.path}
                    version={versions.get(entry.path) ?? ''}
                    size={list ? 20 : view.size}
                    folder={isDir}
                  />
                )}
                {(list || view.showNames) && <span className="asset-name">{isDir ? entry.name : assetDisplayName(entry.path)}</span>}
                {list ? (
                  <>
                    <span className="asset-col-type">{typeName}</span>
                    <span className="asset-col-labels">
                      {fileLabels.map((l) => (
                        <span key={l} className="asset-label">
                          {l}
                        </span>
                      ))}
                    </span>
                    <span className="asset-col-dir">{searching ? parentOf(entry.path) : ''}</span>
                    <span className="asset-col-size">{isDir ? '' : formatBytes(entry.size)}</span>
                    <span className="asset-col-date" title={entry.modifiedAt}>
                      {entry.modifiedAt ? new Date(entry.modifiedAt).toLocaleDateString() : ''}
                    </span>
                  </>
                ) : (
                  kind !== 'folder' && kind !== 'file' && <span className="asset-type">{typeName}</span>
                )}
                {fav && <Icon name="starFilled" size={10} className="asset-fav" />}
              </button>
            );
          })}
          {!loading && items.length === 0 && (
            <p className="muted assets-empty">
              {section.kind === 'favorites'
                ? 'No favorites yet: right-click an asset → Add to favorites.'
                : searching
                  ? 'No matching assets.'
                  : 'This folder is empty. Right-click or use + to create an asset.'}
            </p>
          )}
        </div>
        <footer className="assets-status">
          {selectedEntry ? (
            <>
              <span className="mono assets-status-path">{selectedEntry.path}</span>
              {(labels[selectedEntry.path] ?? []).map((l) => (
                <button key={l} type="button" className="asset-label" onClick={() => setQuery(`l:${l}`)} title={`Show assets labelled ${l}`}>
                  {l}
                </button>
              ))}
              {selectedEntry.kind === 'file' && (
                <button type="button" className="assets-status-btn" onClick={() => void editLabels(selectedEntry.path)} title="Edit labels">
                  <Icon name="tag" size={11} />
                </button>
              )}
            </>
          ) : (
            <span className="muted">{items.length} items</span>
          )}
        </footer>
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.entry)} onClose={() => setMenu(null)} />}
    </div>
  );
}

/** The project's folders as an expandable tree (left column). */
function FolderTree({
  folders,
  rootName,
  current,
  open,
  dropTarget,
  onOpen,
  onToggle,
  dropProps,
}: {
  folders: string[];
  rootName: string;
  current: string | null;
  open: ReadonlySet<string>;
  dropTarget: string | null;
  onOpen: (path: string) => void;
  onToggle: (path: string) => void;
  dropProps: (path: string) => Record<string, unknown>;
}) {
  const rows: { path: string; depth: number; hasChildren: boolean }[] = [];
  const children = (parent: string) => folders.filter((f) => parentOf(f) === parent);
  const walk = (parent: string, depth: number) => {
    for (const f of children(parent)) {
      const hasChildren = children(f).length > 0;
      rows.push({ path: f, depth, hasChildren });
      if (open.has(f)) walk(f, depth + 1);
    }
  };
  walk('', 1);
  const row = (path: string, depth: number, hasChildren: boolean, label: string) => (
    <div
      key={path || '__root'}
      className={`assets-tree-row${current === path ? ' is-active' : ''}${dropTarget === path ? ' is-drop' : ''}`}
      style={{ paddingLeft: 4 + depth * 12 }}
      {...dropProps(path)}
    >
      <button type="button" className="assets-tree-caret" tabIndex={-1} onClick={() => hasChildren && onToggle(path)}>
        {hasChildren && <Icon name={open.has(path) ? 'chevronDown' : 'chevronRight'} size={10} />}
      </button>
      <button type="button" className="assets-tree-label" onClick={() => onOpen(path)} title={path || rootName}>
        <Icon name={current === path ? 'folderOpen' : 'folder'} size={13} />
        <span>{label}</span>
      </button>
    </div>
  );
  return (
    <div className="assets-tree" role="tree">
      {row('', 0, folders.length > 0, rootName)}
      {open.has('') && rows.map((r) => row(r.path, r.depth, r.hasChildren, r.path.slice(r.path.lastIndexOf('/') + 1)))}
    </div>
  );
}
